import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  errorModal,
  receiveError,
  successModal,
} from 'utils/showSuccessErrorModal';
import { settingsSaga } from './saga';
import {
  AddWebhhokType,
  BizEmployee,
  CreateBizEmployeeType,
  EmbeddedSignUpType,
  InviteResult,
  PayloadRolePermission,
  SettingsState,
  WhatsappRegisterType,
} from './types';
import { CustomError, CustomSuccess } from 'utils/types/success-error-types';
import { QuickReplyMessageType } from 'app/pages/Inbox/slice/types';
import { isEmployeeDeletedError, SignUpType } from '@heltarchat/shared';

export const initialState: SettingsState = {
  isWhatsappRegister: false,
  isEmbeddedSignUp: false,
  isCreateBizEmployee: false,
  createBizEmployeeError: false,
  isFetchBizEmployee: false,
  isUpdatingQuickReply: false,
  isGenerateApiKey: false,
  isAddOrUpdateWebhook: false,
  isWebhookDelete: false,
  bizEmployees: [],
  selectedSignUpType: SignUpType.ROUTE_MOBILE,
};

const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    whatsappRegisterStart(state, _action: PayloadAction<WhatsappRegisterType>) {
      state.isWhatsappRegister = true;
    },
    whatsappRegisterSuccess(state, action: PayloadAction<CustomSuccess<void>>) {
      state.isWhatsappRegister = false;
      state.isEmbeddedSignUp = false;
      successModal(action.payload.message);
    },
    whatsappRegisterFailure(state, action: PayloadAction<CustomError>) {
      state.isWhatsappRegister = false;
      state.isEmbeddedSignUp = false;
      errorModal(action.payload.errorMessage);
      receiveError(action.payload);
    },

    whatsappEmbededSignUpStart(
      state,
      _action: PayloadAction<EmbeddedSignUpType>,
    ) {
      state.isEmbeddedSignUp = true;
    },

    createBizEmployeeStart(
      state,
      _action: PayloadAction<CreateBizEmployeeType>,
    ) {
      state.isCreateBizEmployee = true;
      state.createBizEmployeeError = false;
      // Clear any prior invite link so a failed attempt can't surface a stale one.
      state.inviteLink = undefined;
      state.inviteEmailSent = undefined;
      state.deletedEmployeeEmail = undefined;
    },
    createBizEmployeeSuccess(
      state,
      action: PayloadAction<CustomSuccess<BizEmployee & Partial<InviteResult>>>,
    ) {
      state.isCreateBizEmployee = false;
      state.createBizEmployeeError = false;
      // Stash the accept-invite link so the create form can show a copy-forward
      // panel instead of just closing (esp. when emailSent is false).
      state.inviteLink = action.payload.data?.inviteLink;
      state.inviteEmailSent = action.payload.data?.emailSent;

      state.bizEmployees = state.bizEmployees?.length
        ? state.bizEmployees.find(e => e.email === action.payload.data?.email)
          ? state.bizEmployees.map(e =>
              e.email === action.payload.data.email ? action.payload.data : e,
            )
          : [action.payload.data, ...state.bizEmployees]
        : [action.payload.data];

      successModal(action.payload.message);
    },
    createBizEmployeeFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      state.createBizEmployeeError = true;
      /*
       * A removed member isn't a failed invite, it's a recoverable one: keep the
       * email so the form can put up a banner that stays until it's acted on. A
       * toast fades in five seconds, which is how admins end up asking support
       * for a "workaround" instead of pressing Restore.
       */
      if (isEmployeeDeletedError(action.payload.errorRaw)) {
        state.deletedEmployeeEmail = action.payload.errorRaw.email;
        return;
      }
      errorModal(action.payload.errorMessage);
    },

    resendBizEmployeeInviteStart(
      state,
      _action: PayloadAction<{ emailId: string }>,
    ) {
      state.isCreateBizEmployee = true;
      // Clear the prior link so a failed resend doesn't surface a stale one.
      state.inviteLink = undefined;
      state.inviteEmailSent = undefined;
    },
    resendBizEmployeeInviteSuccess(
      state,
      action: PayloadAction<CustomSuccess<Partial<InviteResult>>>,
    ) {
      state.isCreateBizEmployee = false;
      state.inviteLink = action.payload.data?.inviteLink;
      state.inviteEmailSent = action.payload.data?.emailSent;
      successModal(action.payload.message);
    },
    resendBizEmployeeInviteFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    generateApiKeyStart(state, _action: PayloadAction<{ expiresIn: number }>) {
      state.isGenerateApiKey = true;
    },
    generateApiKeySuccess(state, action: PayloadAction<CustomSuccess<void>>) {
      state.isGenerateApiKey = false;
      successModal(action.payload.message);
    },
    generateApiKeyFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    addOrUpdateWebhookUrlStart(
      state,
      _action: PayloadAction<{
        urls: AddWebhhokType[];
        message?: string;
      }>,
    ) {
      state.isAddOrUpdateWebhook = true;
    },
    addOrUpdateWebhookUrlSuccess(
      state,
      action: PayloadAction<CustomSuccess<void>>,
    ) {
      state.isAddOrUpdateWebhook = false;
      successModal(action.payload.message);
    },
    addOrUpdateWebhookUrlFailure(state, action: PayloadAction<CustomError>) {
      state.isAddOrUpdateWebhook = false;
      errorModal(action.payload.errorMessage);
    },

    deleteWebhookUrlStart(state, _action: PayloadAction<{ url: string }>) {
      state.isWebhookDelete = true;
    },
    deleteWebhookUrlSuccess(state, action: PayloadAction<CustomSuccess<void>>) {
      state.isWebhookDelete = false;
      successModal(action.payload.message);
    },
    deleteWebhookUrlFailure(state, action: PayloadAction<CustomError>) {
      state.isWebhookDelete = false;
      errorModal(action.payload.errorMessage);
    },

    fetchBizEmployeesStart(
      state,
      action: PayloadAction<{ silent?: boolean } | undefined>,
    ) {
      // A silent refetch (e.g. right after creating an employee) skips the
      // full-screen loader so it doesn't unmount the still-open create form
      // before its close-on-success effect can run.
      if (!action?.payload?.silent) state.isFetchBizEmployee = true;
    },
    fetchBizEmployeesSuccess(state, action: PayloadAction<BizEmployee[]>) {
      state.isFetchBizEmployee = false;
      state.bizEmployees = action.payload;
    },
    fetchBizEmployeesFailure(state, action: PayloadAction<CustomError>) {
      state.isFetchBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    updateBizEmployeeStart(
      state,
      _action: PayloadAction<{ emailId: string; role: string }>,
    ) {
      state.isCreateBizEmployee = true;
    },
    updateBizEmployeeSuccess(
      state,
      action: PayloadAction<CustomSuccess<BizEmployee>>,
    ) {
      state.isCreateBizEmployee = false;
      state.bizEmployees = state.bizEmployees?.map(e =>
        e.email === action.payload.data.email ? action.payload.data : e,
      );
      successModal(action.payload.message);
    },
    updateBizEmployeeFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    deleteBizEmployeeStart(state, _action: PayloadAction<{ emailId: string }>) {
      state.isCreateBizEmployee = true;
    },
    deleteBizEmployeeSuccess(
      state,
      action: PayloadAction<CustomSuccess<{ email: string; status?: string }>>,
    ) {
      state.isCreateBizEmployee = false;
      // A cancelled invite is hard-deleted (row gone); an active login is only
      // soft-deleted, so keep it in the roster marked Deleted (restorable by an
      // OWNER). The server stamps the real deleted_at; this is a placeholder
      // until the next fetch.
      state.bizEmployees =
        action.payload.data.status === 'invited'
          ? state.bizEmployees?.filter(
              e => e.email !== action.payload.data.email,
            )
          : state.bizEmployees?.map(e =>
              e.email === action.payload.data.email
                ? { ...e, deletedAt: new Date().toISOString() }
                : e,
            );
      successModal(action.payload.message);
    },
    deleteBizEmployeeFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    /*
     * The banner lives here but the form that owns it does not, so closing the
     * create panel has to say so explicitly — otherwise the next Create opens
     * showing the previous account, with a live Restore for an email nobody
     * typed.
     */
    clearDeletedEmployeeBanner(state) {
      state.deletedEmployeeEmail = undefined;
    },

    restoreBizEmployeeStart(
      state,
      _action: PayloadAction<{ emailId: string }>,
    ) {
      state.isCreateBizEmployee = true;
    },
    restoreBizEmployeeSuccess(
      state,
      action: PayloadAction<CustomSuccess<BizEmployee>>,
    ) {
      state.isCreateBizEmployee = false;
      state.deletedEmployeeEmail = undefined;
      state.bizEmployees = state.bizEmployees?.map(e =>
        e.email === action.payload.data.email
          ? { ...e, ...action.payload.data, deletedAt: null }
          : e,
      );
      successModal(action.payload.message);
    },
    restoreBizEmployeeFailure(state, action: PayloadAction<CustomError>) {
      state.isCreateBizEmployee = false;
      errorModal(action.payload.errorMessage);
    },

    updateRolePermissionStart(
      _state,
      _action: PayloadAction<PayloadRolePermission[]>,
    ) {},

    updateQuickRepliesStart(
      state,
      _action: PayloadAction<{
        updatedReplies: QuickReplyMessageType[];
        employeeEmail: string;
      }>,
    ) {
      state.isUpdatingQuickReply = true;
    },
    updateBizEmployeeKeyValueSuccess(
      state,
      action: PayloadAction<{ email: string; data: Partial<BizEmployee> }>,
    ) {
      const employeeIndex = state.bizEmployees.findIndex(
        emp => emp.email === action.payload.email,
      );
      if (employeeIndex !== -1) {
        state.bizEmployees[employeeIndex] = {
          ...state.bizEmployees[employeeIndex],
          ...action.payload.data,
        };
      }
      state.isUpdatingQuickReply = false;
    },
    updateBizEmployeeKeyValueFailure(
      state,
      action: PayloadAction<CustomError>,
    ) {
      state.isUpdatingQuickReply = false;
      errorModal(action.payload.errorMessage);
    },
    setSelectedSignUpType(state, action: PayloadAction<string>) {
      state.selectedSignUpType =
        action.payload as SettingsState['selectedSignUpType'];
    },
  },
});

export const { actions: settingsActions } = slice;
export const settingsReducer = slice.reducer;

export const useSettingsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: settingsSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useSettingsSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
